import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdDeleteForever } from "react-icons/md";
import Loader from '../../components/Global/Loader';
import { FaEdit } from 'react-icons/fa';
function Universities() {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [universityToDelete, setUniversityToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/university/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      // Add console.log to check the response structure
      // console.log('API Response:', response.data.data);

      // Check if response.data is an array, if not, extract the array from the correct pro
      setUniversities(response.data.data);
    } catch (error) {
      // console.error('Error fetching universities:', error);
      setError(error.response?.data?.message || 'Failed to fetch universities');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/adduni?id=${id}`);
  };

  const handleDelete = (university) => {
    setUniversityToDelete(university);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    // console.log(universityToDelete);

    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/university/${universityToDelete}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setShowDeleteModal(false);
      setUniversityToDelete(null);
      fetchUniversities(); // Refresh the list
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete university');
    } finally {
      setLoading(false);
    }
  };

  const filteredUniversities = universities.filter((university) => {
    const searchTerm = searchQuery.toLowerCase();
    return (
      university.universityId?.toLowerCase().includes(searchTerm) ||
      university.universityName?.toLowerCase().includes(searchTerm) ||
      university.website?.toLowerCase().includes(searchTerm) ||
      university.region?.toLowerCase().includes(searchTerm) ||
      university.cityTown?.toLowerCase().includes(searchTerm)
    );
  });

  return (
    <div id="main-content" className="relative h-screen pl-36 mt-16 w-full p-8">
      {loading && <Loader />}

      {/* Add the delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[30%]">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-semibold">
                {universityToDelete?.universityName}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUniversityToDelete(null);
                }}
                className="px-4 py-2 text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Universities</h1>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search universities..."
                className="py-2 px-3 w-64 text-sm text-gray-600 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 transition duration-300 shadow-sm"                style={{ borderBottomWidth: "3px", borderBottomColor: "#3b82f6" }}
              />
              <i className="fas fa-search absolute right-3 top-3 text-gray-400"></i>
            </div>
            <button
              className="px-4 py-2 font-semibold text-white rounded bg-blue-500 hover:bg-blue-600 flex items-center"
              onClick={() => navigate('/admin/adduni')}
            >
              <i className="fas fa-plus mr-2"></i>
              Add University
            </button>
          </div>
        </div>
        <table className="w-full table-auto border-t border-b border-gray-300">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="px-4 py-2 text-center text-gray-400">University ID</th>
              <th className="px-4 py-2 text-center text-gray-400">University Name</th>
              <th className="px-4 py-2 text-center text-gray-400">Website</th>
              <th className="px-4 py-2 text-center text-gray-400">Region</th>
              <th className="px-4 py-2 text-center text-gray-400">City/Town</th>
              <th className="px-4 py-2 text-center text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(filteredUniversities) && filteredUniversities.length > 0 ? (
              filteredUniversities.map((university) => (
                <tr
                  key={university._id}
                  className="border-b border-gray-300 cursor-pointer text-center hover:bg-gray-50"
                >
                  <td className="px-4 py-4 text-sm text-gray-700">{university.universityId}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{university.universityName}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">
                    <a
                      href={university.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-600"
                    >
                      Visit Website
                    </a>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">{university.region}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{university.cityTown}</td>
                  <td className="px-4 pt-4 flex items-center justify-center space-x-3">
                    <FaEdit
                      className="text-blue-500 mr-1 cursor-pointer hover:text-blue-600"
                      onClick={() => handleEdit(university._id)}
                      size={20}
                    />
                    <MdDeleteForever
                      className="text-red-500 cursor-pointer hover:text-red-600"
                      onClick={() => handleDelete(university._id)}
                      size={22}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <i className="fas fa-search text-4xl mb-3 text-gray-300"></i>
                    <p className="text-lg">
                      {searchQuery
                        ? "No universities found matching your search criteria"
                        : "No universities available"}
                    </p>
                    <p className=" text-gray-400 mt-1">
                      {searchQuery
                        ? "Try adjusting your search terms"
                        : "Universities will appear here once added"}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {error && (
          <div className="text-center text-red-500 mt-4">
            {error}
          </div>
        )}

        {!loading && (!universities || universities.length === 0) && (
          <div className="text-center text-gray-500 mt-4">
            No universities found
          </div>
        )}
      </div>
    </div>
  );
}

export default Universities;